import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';

// Tooltip positioning helper
const getTooltipPosition = (element, placement) => {
  if (!element) return { top: 0, left: 0 };
  
  const rect = element.getBoundingClientRect();
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;
  const tooltipWidth = 320;
  const tooltipHeight = 200;
  const gap = 12;
  
  let top, left;
  
  switch (placement) {
    case 'top':
      top = rect.top + scrollY - tooltipHeight - gap;
      left = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
      break;
    case 'bottom':
      top = rect.bottom + scrollY + gap;
      left = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
      break;
    case 'left':
      top = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2);
      left = rect.left + scrollX - tooltipWidth - gap;
      break;
    case 'right':
      top = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2);
      left = rect.right + scrollX + gap;
      break;
    default:
      top = rect.bottom + scrollY + gap;
      left = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2);
  }
  
  // Keep tooltip in viewport
  const maxLeft = window.innerWidth - tooltipWidth - 20;
  const minLeft = 20;
  left = Math.max(minLeft, Math.min(maxLeft, left));
  
  return { top, left };
};

// Highlight overlay component
const HighlightOverlay = ({ targetRect }) => {
  if (!targetRect) return null;
  
  const padding = 8;
  const borderRadius = 8;
  
  return (
    <>
      {/* Dark overlay with cutout */}
      <div 
        className="fixed inset-0 z-[9998]"
        style={{
          background: 'rgba(0, 0, 0, 0.5)',
          clipPath: `polygon(
            0% 0%, 
            0% 100%, 
            ${targetRect.left - padding}px 100%, 
            ${targetRect.left - padding}px ${targetRect.top - padding}px, 
            ${targetRect.right + padding}px ${targetRect.top - padding}px, 
            ${targetRect.right + padding}px ${targetRect.bottom + padding}px, 
            ${targetRect.left - padding}px ${targetRect.bottom + padding}px, 
            ${targetRect.left - padding}px 100%, 
            100% 100%, 
            100% 0%
          )`
        }}
      />
      {/* Highlight border */}
      <div
        className="fixed z-[9999] pointer-events-none"
        style={{
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + (padding * 2),
          height: targetRect.height + (padding * 2),
          border: '3px solid #1E3A5F',
          borderRadius: borderRadius,
          boxShadow: '0 0 0 4px rgba(38, 169, 108, 0.3), 0 0 20px rgba(38, 169, 108, 0.4)'
        }}
      />
    </>
  );
};

// Tooltip component
const Tooltip = ({ step, currentStep, totalSteps, onNext, onPrev, onClose, onLearnMore, position }) => {
  return (
    <div 
      className="fixed z-[10000] w-80 bg-white rounded-xl shadow-2xl border-2 border-[#1E3A5F] overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      style={{ top: position.top, left: position.left }}
      data-testid="tutorial-tooltip"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#152a47] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm font-medium">
            {currentStep} of {totalSteps}
          </span>
        </div>
        <button 
          onClick={onClose}
          className="text-white/80 hover:text-white transition-colors"
          data-testid="tutorial-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-lg text-[#000501] mb-2">{step.title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{step.content}</p>
      </div>
      
      {/* Footer */}
      <div className="px-4 pb-4 flex items-center justify-between">
        <button
          onClick={onLearnMore}
          className="text-sm text-[#1E3A5F] hover:text-[#152a47] font-medium flex items-center gap-1"
          data-testid="tutorial-learn-more-btn"
        >
          <BookOpen className="w-4 h-4" />
          Learn More
        </button>
        
        <div className="flex items-center gap-2">
          {currentStep > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={onPrev}
              className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/10"
              data-testid="tutorial-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
          <Button
            size="sm"
            onClick={onNext}
            className="bg-[#1E3A5F] hover:bg-[#152a47] text-white"
            data-testid="tutorial-next-btn"
          >
            {currentStep === totalSteps ? 'Finish' : 'Next'}
            {currentStep < totalSteps && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="h-1 bg-gray-100">
        <div 
          className="h-full bg-[#1E3A5F] transition-all duration-300"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );
};

// Main Interactive Tutorial Component
export const InteractiveTutorial = ({ 
  tutorial, 
  isOpen, 
  onClose, 
  onLearnMore 
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetElement, setTargetElement] = useState(null);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const resizeObserverRef = useRef(null);
  
  const currentStep = tutorial?.steps?.[currentStepIndex];
  const totalSteps = tutorial?.steps?.length || 0;
  
  // Find and highlight target element
  const updateTargetElement = useCallback(() => {
    if (!currentStep?.target) return;
    
    const element = document.querySelector(currentStep.target);
    setTargetElement(element);
    
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect(rect);
      setTooltipPosition(getTooltipPosition(element, currentStep.placement));
      
      // Scroll element into view if needed
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentStep]);
  
  // Update on step change
  useEffect(() => {
    if (isOpen && currentStep) {
      // Small delay to allow DOM to update
      const timer = setTimeout(updateTargetElement, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentStep, updateTargetElement]);
  
  // Update on resize/scroll
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdate = () => {
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setTargetRect(rect);
        setTooltipPosition(getTooltipPosition(targetElement, currentStep?.placement));
      }
    };
    
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);
    
    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [isOpen, targetElement, currentStep?.placement]);
  
  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setCurrentStepIndex(0);
      setTargetElement(null);
      setTargetRect(null);
    }
  }, [isOpen]);
  
  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStepIndex, totalSteps]);
  
  const handleNext = () => {
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      onClose();
    }
  };
  
  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };
  
  const handleLearnMore = () => {
    onClose();
    if (onLearnMore) {
      onLearnMore(tutorial.id);
    }
  };
  
  if (!isOpen || !tutorial || !currentStep) return null;
  
  return createPortal(
    <>
      <HighlightOverlay targetRect={targetRect} />
      <Tooltip
        step={currentStep}
        currentStep={currentStepIndex + 1}
        totalSteps={totalSteps}
        onNext={handleNext}
        onPrev={handlePrev}
        onClose={onClose}
        onLearnMore={handleLearnMore}
        position={tooltipPosition}
      />
    </>,
    document.body
  );
};

export default InteractiveTutorial;
